import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Eye, Printer, Download, RotateCcw, Loader2 } from "lucide-react";
import { Receipt80mm } from "./POS";

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [settings, setSettings] = useState(null);
  const [returnFor, setReturnFor] = useState(null);
  const [retQty, setRetQty] = useState({});
  const [retReason, setRetReason] = useState("");
  const [returning, setReturning] = useState(false);
  const { user } = useAuth();

  const openReturn = (inv) => {
    setReturnFor(inv);
    setRetQty({});
    setRetReason("");
    setOpen(false);
  };

  const submitReturn = async () => {
    const items = returnFor.items
      .filter((it) => (Number(retQty[it.medicine_id + "_" + it.unit]) || 0) > 0)
      .map((it) => ({
        medicine_id: it.medicine_id, name: it.name, unit: it.unit,
        quantity: Number(retQty[it.medicine_id + "_" + it.unit]),
        pack_size: it.pack_size, unit_price: it.unit_price, gst_percent: it.gst_percent,
      }));
    if (items.length === 0) return toast.error("Enter a quantity to return");
    setReturning(true);
    try {
      const { data } = await api.post(`/invoices/${returnFor.id}/return`, { items, reason: retReason });
      toast.success(`Credit note ${data.return_no} — refund ₹${data.refund_total}`);
      setReturnFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Return failed");
    } finally {
      setReturning(false);
    }
  };

  const load = async () => {
    const { data } = await api.get("/invoices", { params: q ? { q } : {} });
    setItems(data);
  };

  useEffect(() => { load(); api.get("/settings").then(({ data }) => setSettings(data)); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const view = async (inv) => {
    const { data } = await api.get(`/invoices/${inv.id}`);
    setSelected(data); setOpen(true);
  };

  return (
    <div className="space-y-5" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Invoices &amp; History</h1>
          <p className="text-sm text-slate-500">{items.length} invoices — reprint or review any bill.</p>
        </div>
        <Button variant="outline" data-testid="export-invoices-btn" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/invoices`, "_blank")}>
          <Download className="w-4 h-4 mr-2" /> Export Excel
        </Button>
      </div>

      <Card className="p-4 border-slate-200">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="invoice-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by invoice no, customer, phone…" className="pl-9" />
        </div>
      </Card>

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((inv) => (
                <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <TableCell className="font-mono-num font-bold">
                    {inv.invoice_no}
                    {["returned", "partial_return", "cancelled"].includes(inv.status) && (
                      <Badge data-testid={`invoice-status-${inv.id}`} className={`ml-2 text-[10px] uppercase ${inv.status === "cancelled" ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}`}>
                        {inv.status === "partial_return" ? "partial return" : inv.status}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{inv.customer_name}</div>
                    <div className="text-xs text-slate-500">{inv.customer_phone}</div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{new Date(inv.created_at).toLocaleString("en-IN")}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="uppercase text-xs">{inv.payment_mode}</Badge>
                    {inv.source && inv.source !== "pos" && <Badge className="ml-1.5 text-[10px] bg-indigo-100 text-indigo-700 hover:bg-indigo-100 uppercase" data-testid={`invoice-source-${inv.id}`}>{inv.source}</Badge>}
                  </TableCell>
                  <TableCell className="text-right font-mono-num font-bold text-emerald-900">₹{inv.grand_total.toFixed(2)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => view(inv)} data-testid={`view-inv-${inv.id}`}><Eye className="w-4 h-4 mr-1" /> View</Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No invoices yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="font-display">Invoice {selected?.invoice_no}</DialogTitle>
          </DialogHeader>
          {selected && <Receipt80mm invoice={selected} settings={settings} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            {selected && !["returned", "cancelled"].includes(selected.status) && ["admin", "manager"].includes(user?.role) && (
              <Button variant="outline" data-testid="return-invoice-btn" onClick={() => openReturn(selected)} className="border-red-200 text-red-700 hover:bg-red-50">
                <RotateCcw className="w-4 h-4 mr-2" /> Return
              </Button>
            )}
            <Button onClick={() => window.print()} className="bg-emerald-900 hover:bg-emerald-800"><Printer className="w-4 h-4 mr-2" /> Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!returnFor} onOpenChange={(o) => !o && setReturnFor(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="return-dialog">
          <DialogHeader><DialogTitle className="font-display">Return / Refund — {returnFor?.invoice_no}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {returnFor?.items.map((it) => {
              const key = it.medicine_id + "_" + it.unit;
              return (
                <div key={key} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{it.name}</div>
                    <div className="text-xs text-slate-500">{it.unit} • sold {it.quantity} • ₹{Number(it.unit_price).toFixed(2)}</div>
                  </div>
                  <Input
                    data-testid={`return-qty-${key}`}
                    type="number" min="0" max={it.quantity}
                    value={retQty[key] ?? ""}
                    onChange={(e) => setRetQty({ ...retQty, [key]: Math.min(Number(e.target.value) || 0, it.quantity) })}
                    className="h-9 w-20 font-mono-num" placeholder="0"
                  />
                </div>
              );
            })}
            <div>
              <Label className="text-xs">Return reason</Label>
              <Input data-testid="return-reason" value={retReason} onChange={(e) => setRetReason(e.target.value)} className="mt-1 h-10" placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnFor(null)}>Cancel</Button>
            <Button onClick={submitReturn} disabled={returning} data-testid="return-submit" className="bg-red-600 hover:bg-red-700 text-white">
              {returning ? <Loader2 className="w-4 h-4 animate-spin" /> : "Process Return"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
